import React, { useEffect, useMemo, useRef, useState } from 'react';
import { player } from '../game/player';
import * as THREE from 'three';
import { useGLTF } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import { ktx2Extend } from '../utils/ktx2';
import { assetUrl } from '../services/assets';
import { useWorldStore } from '../state/worldStore';
import { listWorldObjects } from '../services/worldObjects';
import { listWorldAssets, assetUrlFor } from '../services/worldAssets';
import { ROOMS, TOWN_LOCATIONS, ENTRY_VIA_PORTAL } from '../rooms';
import CameraBasisProbe from './CameraBasisProbe';
import { cutsceneBridge } from '../city/cutscene';
import { derivedPortals } from '../city/venueRegistry';
import { roomSpace } from '../state/interiorFloor';
import { roomBody } from '../state/roomBody';
import { doorZones } from '../state/doorZones';

const roomP = new THREE.Vector3();

/**
 * A world point in a portal's own frame — the frame its trigger box is in. A
 * doorway measured in ROOM space (a room's own exit) carries the point into the
 * room first (a sited room is moved and turned with its building); then the
 * translation is undone, then the rotation. Doing only the translation is the
 * bug that makes a rotated portal trigger off to one side of where its box is
 * drawn. ONE conversion: the trigger test below and the doors the walls keep
 * clear of (state/doorZones) both use it.
 */
function toPortalLocal(row, wx, wy, wz) {
  if (row.data?.roomSpace && roomSpace.object) {
    roomP.set(wx, wy, wz); roomSpace.object.worldToLocal(roomP);
    wx = roomP.x; wy = roomP.y; wz = roomP.z;
  }
  const a = -(row.rotY || 0);
  const c = Math.cos(a); const s = Math.sin(a);
  const lx = wx - row.pos[0]; const lz = wz - row.pos[2];
  return { x: lx * c + lz * s, y: wy - row.pos[1], z: -lx * s + lz * c };
}
const EYE_Y = 0.75;   // the street eye height (PlanetScene EYE) for a third-person body

// Portals — the doors between the street and the places under it.
//
// A subway entrance is not a venue's exterior, it is a PLACED INSTANCE of a
// shared asset: there is one entrance model and there will be one on every other
// corner. That distinction decides the whole shape of this. A venue `shell` is
// one-per-venue and could never be the tenth entrance; a `world_object` with
// kind 'portal' is a row, so the tenth costs a row — and because every one of
// them carries the same `asset_id`, they can be issued as a single instanced
// draw rather than ten.
//
// It is also what the spec meant by `interior_ref`: "if enterable, which
// interior world" (§1). `ref` is the room id this opens into.
//
// WHY NOT A MARKER PIN. VenueMarkers floats a clickable label over a venue,
// which is fine for a shopfront and undercuts a building whose entire design is
// a mouth you walk into. The trigger is a volume you enter — Design Law 1, the
// function disappears into the geography.

// Where the trigger sits when a portal has not been tuned yet: a box in front of
// the entrance, at head height, big enough to walk into without aiming. Tuned
// per portal in the panel and saved to `data.trigger`, because where the mouth
// is depends on the model and no default can know that.
const DEFAULT_TRIGGER = { x: 0, y: 0.5, z: 0, w: 2, h: 2, d: 2 };

const trigOf = (o) => ({ ...DEFAULT_TRIGGER, ...(o.data?.trigger || {}) });

// WHAT A PORTAL OPENS ONTO — a room, or another portal.
//
// Transit is why this is two things and not one. Entering the underground is a
// portal that opens onto a ROOM. Coming back up is not: the whole point of a
// subway is that you surface somewhere you did not start, so the platform's exit
// opens onto ANOTHER PORTAL — the station at the far end — and you step out of
// that one, on that street.
//
// Targeting the portal rather than a coordinate is what makes it a network. Move
// the destination station and every route into it follows, because the route
// points at the station rather than at where the station used to be. It also
// chains: if the far end grows its own hallway, the exit points at a second room
// whose exit points at the street portal, and nothing here changes.
const targetOf = (o) => o.data?.target || { kind: 'room', ref: o.ref };

// You surface IN FRONT of the station, facing away from it — the direction a
// person emerging actually looks, and it puts the new neighbourhood in view
// instead of the doorway you just came out of.
const EMERGE = 3;
const arrivalAt = (portal, eye) => {
  const rot = portal.rotY || 0;
  // The placement tool stores rotY as atan2(-fx, -fz) of the camera that placed
  // it, so this inverts that to recover the direction the entrance faces.
  const fx = -Math.sin(rot); const fz = -Math.cos(rot);
  const x = portal.pos[0] - fx * EMERGE;
  const z = portal.pos[2] - fz * EMERGE;
  return { pos: [x, eye, z], look: [x - fx * 6, eye, z - fz * 6] };
};

// WHERE A ROOM'S OWN DOORWAY LETS YOU OUT, when the street knows no door for it
// (2026-10-31, the Corner Market). A venue the town built comes out at its
// site's door and a room with a street door placed by hand in front of that
// (PlanetScene's street pose), but the market — a built-in room standing in its
// building — had only "the nearest pavement to the building's middle", which
// can be round the back. Its doorway IS its door: a step past the box, away
// from the room's middle, carried out through the room's live transform, facing
// the street (arrivalAt's rule). Null = let the street pose decide, as before.
const DOOR_STEP = 0.9;
const doorP = new THREE.Vector3();
function outsideOf(row) {
  const st = useWorldStore.getState();
  const room = ROOMS[st.interior];
  const space = roomSpace.object;
  if (!space || !row.data?.roomSpace || room?.placement !== 'sited' || room.registry || st.streetDoors?.[st.interior]) return null;
  const t = trigOf(row);
  const a = row.rotY || 0; const c = Math.cos(a); const s = Math.sin(a);
  // The box's centre and its thin axis, in room space (rotation.y turns local
  // +x to (cos, −sin) and +z to (sin, cos)).
  const cx = row.pos[0] + t.x * c + t.z * s;
  const cz = row.pos[2] - t.x * s + t.z * c;
  const [ax, az, half] = t.d <= t.w ? [s, c, t.d / 2] : [c, -s, t.w / 2];
  const out = Math.sign(ax * cx + az * cz) || 1;   // away from the room's middle
  const step = (k) => space.localToWorld(doorP.set(cx + ax * out * k, row.pos[1], cz + az * out * k)).clone();
  const p = step(half + DOOR_STEP); const ahead = step(half + DOOR_STEP + 6);
  return { pos: [p.x, EYE_Y, p.z], look: [ahead.x, EYE_Y, ahead.z] };
}

// The asset's own origin is the placement point, and its y=0 is the pavement.
//
// Both are the street-prop convention, and both matter here: this entrance
// carries five metres of stairwell BELOW y=0, so grounding it the way a venue is
// grounded — drop the lowest point to zero — would bury the street and hang the
// canopy two units in the air. `detectFloor` cannot save us either; it looks for
// a wide flat mesh near the BOTTOM, which in this model is the floor of the
// shaft. The model already says where the ground is. Believe it.
function PortalModel({ url, fit }) {
  const gl = useThree((s) => s.gl);
  const ktx2 = useMemo(() => ktx2Extend(gl), [gl]);
  const { scene } = useGLTF(assetUrl(url), true, true, ktx2);

  const model = useMemo(() => {
    const m = scene.clone(true);
    // Same guard the shells carry: a baked light that escapes into the open city
    // overwhelms the day/night rig for everything around it.
    const doomed = [];
    m.traverse((o) => { if (o.isLight) doomed.push(o); });
    for (const l of doomed) l.parent?.remove(l);
    if (doomed.length) console.warn(`[portal] removed ${doomed.length} baked light(s) from ${url}`);

    m.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(m);
    const size = new THREE.Vector3(); box.getSize(size);
    const longest = Math.max(size.x, size.z) || 1;
    const s = fit ? fit / longest : 1;
    m.scale.setScalar(s);
    const g = new THREE.Group();
    g.add(m);
    return g;
  }, [scene, fit, url]);

  return <primitive object={model} />;
}

function Portal({ row, url, editing, selected }) {
  const t = trigOf(row);
  return (
    <group position={row.pos} rotation={[0, row.rotY || 0, 0]}>
      {url ? (
        <React.Suspense fallback={null}>
          <PortalModel url={url} fit={row.size || 6.7} />
        </React.Suspense>
      ) : null}
      {/* The trigger, drawn only while placing. An invisible volume is
          impossible to aim, and every mis-set one reads as "the door is
          broken" rather than "the box is six inches to the left". */}
      {editing && (
        <mesh position={[t.x, t.y, t.z]}>
          <boxGeometry args={[t.w, t.h, t.d]} />
          <meshBasicMaterial color={selected ? '#ffb25e' : '#7dd3ff'} wireframe transparent opacity={selected ? 0.9 : 0.35} />
        </mesh>
      )}
    </group>
  );
}

export default function Portals({ eye = 0.75 }) {
  const [saved, setSaved] = useState([]);
  const [assets, setAssets] = useState([]);
  const interior = useWorldStore((s) => s.interior);
  const enterInterior = useWorldStore((s) => s.enterInterior);
  const exitInterior = useWorldStore((s) => s.exitInterior);
  const setSpawnOverride = useWorldStore((s) => s.setSpawnOverride);
  const editing = useWorldStore((s) => s.adminTool) === 'portal';
  const sel = useWorldStore((s) => s.portalSel);
  const version = useWorldStore((s) => s.portalVersion);

  // EVERY zone's portals, not just this one. A platform exit has to resolve a
  // target standing on a street that is not loaded while you are underground, so
  // filtering the fetch by zone would make the far end unreachable.
  useEffect(() => {
    let dead = false;
    listWorldObjects(null, 'portal').then((r) => { if (!dead) setSaved(r || []); });
    listWorldAssets().then((a) => { if (!dead) setAssets(a || []); });
    return () => { dead = true; };
  }, [version]);

  // THE TOWN'S OWN VENUES HAVE DOORS NOBODY PLACED (city/venueRegistry). The
  // server writes no portal rows for them; each one's door is derived from its
  // site — at site.door, facing the street — and joins the saved ones here, so
  // it fires, warns and resolves exactly like a door placed with the tool. A
  // venue moved by hand takes its door with it; one given a door by hand keeps
  // that one instead.
  const registry = useWorldStore((s) => s.venueRegistry);
  const anchorOverrides = useWorldStore((s) => s.venueAnchorOverrides);
  const all = useMemo(() => {
    const derived = derivedPortals(registry, anchorOverrides, saved);
    // …AND EVERY ROOM'S OWN WAY OUT (2026-10-31, Khari: "When we are leaving the
    // door of a building, we should spawn back in front of it. this is easier
    // than trying to find the 'back to street' button, and more natural").
    // A room that says where its doorway is (rooms.js `exit`, ROOM space) gets a
    // trigger there, inside the room, that leaves it: exitInterior lands you in
    // front of the building's own door (its site's for a venue the town built —
    // HQ's faces the city, not the street). Room space because a sited room
    // stands at its building, turned with it; the test converts (below).
    const exits = Object.entries(ROOMS).filter(([, r]) => r?.exit?.pos && !r.retired).map(([id, r]) => ({
      object_id: `exit:${id}`, zone_id: id, kind: 'portal', ref: 'street',
      pos: r.exit.pos, rotY: r.exit.rotY || 0, size: 1,
      data: {
        trigger: { x: 0, y: (r.exit.size?.[1] ?? 1.6) / 2, z: 0, w: r.exit.size?.[0] ?? 1, h: r.exit.size?.[1] ?? 1.6, d: r.exit.size?.[2] ?? 0.22 },
        target: { kind: 'street' }, roomSpace: true, derived: true,
      },
    }));
    return [...saved, ...derived, ...exits];
  }, [saved, registry, anchorOverrides]);

  // WHERE EACH ROOM'S HAND-PLACED STREET DOOR LETS YOU OUT (worldStore.streetDoors):
  // in front of the street portal that leads into it, facing away, the same pose
  // a portal-to-portal trip arrives at. PlanetScene lands a room's exit there.
  useEffect(() => {
    const doors = {};
    for (const o of saved) {
      if (o.zone_id !== 'street') continue;
      const t = targetOf(o);
      if (t?.kind === 'room' && t.ref && !doors[t.ref]) doors[t.ref] = arrivalAt(o, EYE_Y);
    }
    useWorldStore.getState().setStreetDoors(doors);
  }, [saved]);

  // WHICH VENUES HAVE NO WAY IN.
  //
  // ENTRY_VIA_PORTAL retires the building-as-hit-zone, so a venue with no portal
  // row can no longer be WALKED into. Its marker and the map still work, so this
  // is not a lockout — but walking up to a building and having it ignore you is
  // indistinguishable from a broken click handler, and it is the mode the guard,
  // the doorway and the whole change are about.
  //
  // So say it, by name, once per load. The fix — place a door — is not something
  // anyone would guess from a building that simply does nothing.
  useEffect(() => {
    if (!ENTRY_VIA_PORTAL || !all.length) return;
    const reachable = new Set(all.filter((o) => o.zone_id === 'street').map((o) => (o.data?.target?.ref) || o.ref));
    const orphans = TOWN_LOCATIONS.filter((l) => l.interiorId && !reachable.has(l.interiorId));
    if (!orphans.length) { console.log(`[portal] all ${TOWN_LOCATIONS.length} venue(s) have a door`); return; }
    console.warn(`[portal] ${orphans.length} venue(s) have NO placed door — you cannot walk into them `
      + '(their marker and the map still work): '
      + orphans.map((l) => `${l.name} (${l.interiorId})`).join(', ')
      + ' — place one with ⚙ → Portal, or add ?hitzone=1 to restore the old click-the-building entry for this session.');
  }, [all]);

  // The zone you are standing in: a room id inside, 'street' outside. Only these
  // are drawn and only these can fire.
  const zone = interior || 'street';
  const rows = useMemo(() => all.filter((o) => o.zone_id === zone), [all, zone]);
  // THE DOORS, FOR THE WALLS (state/doorZones): SolidClamp never stops you in or
  // beside a door's trigger, so a door that is a surface (a glass door, a
  // painted one) can still be walked into and taken.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  useEffect(() => {
    doorZones.near = (wx, wy, wz, m = 0.35) => rowsRef.current.some((row) => {
      const t = trigOf(row);
      const p = toPortalLocal(row, wx, wy, wz);
      return Math.abs(p.x - t.x) <= t.w / 2 + m && Math.abs(p.y - t.y) <= t.h / 2 + m && Math.abs(p.z - t.z) <= t.d / 2 + m;
    });
    return () => { doorZones.near = null; };
  }, []);
  const byObjectId = useMemo(() => new Map(all.map((o) => [o.object_id, o])), [all]);

  // THE HOLE THIS STATION STANDS IN.
  //
  // The entrance carries five metres of stairwell below y=0, and until the ground
  // opens under it none of that is visible — the model reads as a canopy sitting
  // on tarmac with its stairs buried. A portal that declares `data.pit` tells the
  // ground to open there, so the station sinks in rather than resting on top.
  //
  // Published rather than fetched by the ground, because the portals are already
  // in hand here. Two fetches would be two chances for the hole and the station
  // to end up in different places.
  const setGroundPits = useWorldStore((s) => s.setGroundPits);
  useEffect(() => {
    const street = saved.filter((o) => o.zone_id === 'street');
    // A station's opening is a rectangle, because the thing standing in it is.
    // A circle either leaves tarmac inside the stairwell's corners or eats the
    // pavement outside its long sides — there is no radius that fits a 6x14
    // entrance. `rotY` travels with it so the hole stays square to the stairs
    // when the station is turned.
    //
    // `r` is still honoured as a square, so pits saved before this reads as
    // something sensible rather than vanishing.
    const pits = street
      .filter((o) => o.data?.pit && (o.data.pit.w > 0 || o.data.pit.r > 0))
      .map((o) => {
        const p = o.data.pit;
        return {
          x: o.pos[0], z: o.pos[2], rotY: o.rotY || 0,
          w: p.w > 0 ? p.w : p.r * 2,
          d: p.d > 0 ? p.d : (p.w > 0 ? p.w : p.r * 2),
        };
      });
    setGroundPits(pits);
    // SAY WHICH OF THE TWO REASONS IT IS.
    //
    // "The ground is still there" has exactly two causes and they need opposite
    // fixes: no pit was ever saved, or the pit is saved and the ground being
    // looked at is not the one that can be cut. Printing whether `data` survived
    // the round trip separates them in one line — and a portal that has a pit
    // radius in the panel but no `data` here means the API is dropping it,
    // which means it has not been restarted.
    if (!street.length) return;
    const withData = street.filter((o) => o.data && Object.keys(o.data).length).length;
    console.log(`[portal] ${street.length} street portal(s), ${withData} carrying data, ${pits.length} ground pit(s)`
      + (pits.length ? `: ${pits.map((p) => `${p.w}x${p.d} @ ${p.x.toFixed(0)},${p.z.toFixed(0)}`).join(' · ')}` : '')
      + (!withData ? ' — NO portal has any `data`. Set a pit radius and if this still reads 0, the API is dropping the field: restart it.' : ''));
  }, [saved, setGroundPits]);

  const byId = useMemo(() => new Map(assets.map((a) => [a.assetId, a])), [assets]);

  // ARMED ONLY AFTER YOU LEAVE.
  //
  // Coming back UP the stairs puts you standing in the trigger you just used, so
  // a naive "am I inside the box" test re-enters immediately and the player is
  // trapped in a loop they cannot walk out of. A portal therefore starts
  // disarmed and only becomes live once the player has been outside it — which
  // also means spawning next to one is safe.
  const armed = useRef(new Map());
  // WHERE YOU WERE LAST FRAME, so the test is a SWEPT one.
  //
  // The trigger used to test a single point, which is fine for a 2-unit box and
  // fails for a door. Street walking is 9 units/sec and running is 2.2× that, so
  // at 60fps a running player moves 0.33 units between frames and at 30fps
  // 0.66 — further than the depth of any box thin enough to sit on an actual
  // doorway without also catching people on the pavement. The door then works at
  // a walk, works when you approach it slowly, and silently fails when you run
  // at it, which is the worst possible way for a door to be broken.
  //
  // So the box is tested against the SEGMENT you travelled, not the point you
  // landed on. This is what makes a thin trigger usable at all — without it,
  // "place it exactly on the door" and "the door opens reliably" are in direct
  // conflict and the only resolution is an over-wide box.
  const prev = useRef(null);
  // A NEW ZONE IS A TELEPORT, AND EVERY DOOR IN IT STARTS DISARMED (2026-10-31).
  // Coming out of a SITED room you stand right beside its street door (the room
  // is in that building), and the line from where you stood inside to where
  // you land outside crosses the door's box: it fired and put you back in. So
  // nothing is swept across a change of zone, and a door you arrive beside must
  // be stepped away from before it opens — the rule above, for every arrival.
  // ⚠ Done INSIDE the frame loop below, not in an effect (2026-10-31: "I am still
  // being thrown back into the HQ"): an effect lands a frame or two after the
  // zone flips, and in those frames the door was armed from where you stood
  // inside and fired on the jump out.
  const seenZone = useRef(zone);
  // …and no door is tested while the camera is being MOVED for you (a fly-in,
  // the hop out of a sited room) nor for a moment after it lands, so the walk has
  // put your body where you landed before a door can see it (in third person the
  // door tests the body, which is stale until then).
  const quietUntil = useRef(0);
  const SETTLE_QUIET_MS = 600;

  const fire = (row) => {
    const t = targetOf(row);
    console.log(`[portal] #${row.object_id} fired → ${t?.kind || '?'}:${t?.ref ?? (t?.kind === 'street' ? 'out' : 'NOTHING')}`);
    // A room's own doorway out (above): back to the street, in front of the
    // building. Only from inside a room — two Portals layers can see one row,
    // and the second must not "leave" again with nowhere to leave from.
    // ⚠ BEFORE the target test below (2026-10-31, Khari: "the user cannot walk
    // out of a room … basically every indoor scene"). The street is not a room
    // or a portal, so this target has no `ref`, and the test refused every
    // room's way out as "has no target" — the box was entered, nothing opened.
    if (t?.kind === 'street') {
      if (useWorldStore.getState().interior) exitInterior(outsideOf(row));
      return;
    }
    if (!t?.ref) {
      console.warn(`[portal] #${row.object_id} has no target. Its \`ref\` is ${JSON.stringify(row.ref)} and data.target is ${JSON.stringify(row.data?.target)} — set one with "Opens onto" in the Portals panel.`);
      return;
    }
    if (t.kind === 'portal') {
      const dest = byObjectId.get(Number(t.ref));
      if (!dest) { console.warn(`[portal] #${row.object_id} points at portal #${t.ref}, which no longer exists`); return; }
      // ARRIVE STANDING AT THE DESTINATION PORTAL, wherever it lives. The pose
      // comes from the destination's own transform, so moving that station moves
      // every route that arrives at it.
      //
      // Two cases, one rule. A portal on the STREET means surfacing: leave the
      // room and land in front of it. A portal in a ROOM means the train pulling
      // in at the far platform — enter that room, but at the spot where the
      // train stops rather than at the room's front door. `enterInterior` clears
      // spawnOverride, so the pose is applied after it, not with it.
      const pose = arrivalAt(dest, eye);
      if (dest.zone_id === 'street') { exitInterior(pose); return; }
      enterInterior(dest.zone_id);
      setSpawnOverride(pose);
      return;
    }
    enterInterior(t.ref);
  };

  useFrame(({ camera }) => {
    // A CUTSCENE HAS THE CAMERA, NOT YOU. Its shots — and a replay's ride behind
    // the lead past the venues — went through doors' trigger boxes and opened
    // them: you were pulled into a room mid-episode, where the street (its
    // voices, its people) is not loaded (Khari, 2026-10-24: "when I am asked to
    // view the scene, it is in a room"). No door fires while a scene holds the
    // camera, and nothing is swept across the jump back to where you stood.
    if (cutsceneBridge.active) { prev.current = null; return; }
    if (seenZone.current !== zone) { seenZone.current = zone; prev.current = null; armed.current = new Map(); }
    const nowMs = performance.now();
    if (!useWorldStore.getState().camSettled) quietUntil.current = nowMs + SETTLE_QUIET_MS;
    if (nowMs < quietUntil.current) { prev.current = null; return; }
    const from = prev.current;
    // The BODY walks through doors — in third person the camera trails it by a
    // couple of metres and must not open them on its own (game/player).
    // …and in a third-person ROOM, the body's eye point (state/roomBody), whose
    // height is the room's floor + eye, not the street's.
    const rb = roomBody.on ? roomBody.v : null;
    const cx = rb ? rb.x : (player.third ? player.x : camera.position.x);
    const cy = rb ? rb.y : (player.third ? EYE_Y : camera.position.y);
    const cz = rb ? rb.z : (player.third ? player.z : camera.position.z);
    // A TELEPORT IS NOT A WALK. Entering a room, surfacing from the subway and
    // fast-travelling to an avatar all move the camera hundreds of units in one
    // frame, and sweeping that line would drag you through every door between
    // the two points — arriving somewhere would fire whatever happened to be on
    // the way. Beyond a stride, only the destination counts.
    const step = from ? Math.hypot(cx - from.x, cy - from.y, cz - from.z) : 0;
    const swept = from && step > 0 && step < 5;

    for (const row of rows) {
      const id = row.object_id;
      const t = trigOf(row);
      // World → portal-local (toPortalLocal: into the room if need be, then
      // the translation and the turn undone).
      const toLocal = (wx, wy, wz) => toPortalLocal(row, wx, wy, wz);
      const contains = (p) => Math.abs(p.x - t.x) <= t.w / 2
        && Math.abs(p.y - t.y) <= t.h / 2
        && Math.abs(p.z - t.z) <= t.d / 2;

      const now = toLocal(cx, cy, cz);
      let inside = contains(now);

      // Walk the segment in steps no longer than the box's THINNEST dimension,
      // so nothing can fit between two samples. Rotation and translation are
      // affine, so interpolating in local space is the same line as
      // interpolating in world space — and it converts once per endpoint rather
      // than once per sample. Capped because a low frame rate must cost frames,
      // not a loop that grows without limit.
      if (!inside && swept) {
        const was = toLocal(from.x, from.y, from.z);
        const thinnest = Math.max(0.05, Math.min(t.w, t.h, t.d));
        const n = Math.min(24, Math.ceil(step / thinnest));
        for (let i = 1; i < n && !inside; i += 1) {
          const k = i / n;
          inside = contains({
            x: was.x + (now.x - was.x) * k,
            y: was.y + (now.y - was.y) * k,
            z: was.z + (now.z - was.z) * k,
          });
        }
      }

      // SAY WHEN YOU CROSS THE EDGE. "It stopped warping me" has three causes
      // that look identical from inside the world — you are never entering the
      // box, you are entering it while it is disarmed, or it fires and the
      // target is empty. Logging the transition rather than the state keeps this
      // to one line per crossing instead of one per frame.
      const was = !!armed.current.get(`in:${id}`);
      if (inside !== was) {
        armed.current.set(`in:${id}`, inside);
        console.log(`[portal] #${id} trigger ${inside ? 'ENTERED' : 'left'} · armed=${!!armed.current.get(id)}`
          + ` · you ${camera.position.x.toFixed(1)},${camera.position.y.toFixed(1)},${camera.position.z.toFixed(1)}`
          + ` · box local ${t.x},${t.y},${t.z} size ${t.w}x${t.h}x${t.d} at ${row.pos.map((n) => n.toFixed(1)).join(',')}`);
      }

      if (!inside) { armed.current.set(id, true); continue; }
      if (!armed.current.get(id)) continue;
      armed.current.set(id, false);
      fire(row);
    }

    // Recorded even when there are no rows, so the first frame after a portal
    // loads has a real previous position instead of treating that frame as a
    // teleport.
    if (from) { from.x = cx; from.y = cy; from.z = cz; } else prev.current = { x: cx, y: cy, z: cz };
  });

  return (
    <group>
      {/* "Place ahead of me" reads the camera from a module-level basis, which
          only stays current while something inside the Canvas is updating it. */}
      {editing && <CameraBasisProbe />}
      {rows.map((row) => (
        <Portal
          key={row.object_id}
          row={row}
          url={assetUrlFor(byId.get(row.assetId)) || row.data?.model || null}
          editing={editing}
          selected={sel === row.object_id}
        />
      ))}
    </group>
  );
}
